import {zodResolver} from '@hookform/resolvers/zod';
import {isFrozenTaskDate} from '@nursio-link/common/utils';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {InputLabel} from '@/components/ui/input';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import type {Task} from '@/services/graphqlHooks';
import {useGetTaskQuery, useRejectTaskMutation} from '@/services/graphqlHooks';

import type {RejectTaskFormValues} from '../../utils/tasksFormInputs';
import {rejectTaskFormSchema} from '../../utils/tasksFormInputs';
import {isStandardTask} from '../../utils/tasksHelpers';
import ArchivedTaskNotice from '../ArchivedTaskNotice';
import TaskFormPreview from '../TaskFormPreview';

export type PreviewTaskDialogProps = DialogProps;

const PreviewTaskDialog = ({
  onOpenChange,
  ...props
}: PreviewTaskDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();

  const [isReject, setIsReject] = useState(false);

  const [searchParams] = useSearchParams();
  const id = searchParams.get('task') || '';

  const rejectTask = useRejectTaskMutation(graphqlClient);

  const {data: taskData, isLoading: isTaskLoading} = useGetTaskQuery(
    graphqlClient,
    {id},
    {
      enabled: !!(id && props.open),
      staleTime: 0,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const task = taskData?.getTask as Task;

  const form = useForm<RejectTaskFormValues>({
    resolver: zodResolver(rejectTaskFormSchema),
    defaultValues: {
      rejectionNote: '',
    },
  });

  const handleSubmit = async (values: RejectTaskFormValues) => {
    try {
      await rejectTask.mutateAsync({
        input: {id, rejectionNote: values.rejectionNote || ''},
      });
      toast({
        title: t('Task rejected'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const isArchived = isFrozenTaskDate(task?.date);
  const showRejectionNote =
    isStandardTask(task) && !task?.completedBy && !isArchived;

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader className="md:hidden">
              <DialogTitle>{t('Task')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="py-0">
              {isTaskLoading && <FormSkeleton />}
              {!isTaskLoading && !task && <div>{t('Task not found')}</div>}
              {task && (
                <div className="flex flex-col gap-3">
                  <TaskFormPreview task={task} />
                  {isArchived && <ArchivedTaskNotice />}
                  {showRejectionNote && (
                    <div className="mt-10 flex flex-col gap-10">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="isReject"
                          checked={isReject}
                          onCheckedChange={val => {
                            setIsReject(val as boolean);
                          }}
                        />
                        <InputLabel htmlFor="isReject" className="text-red-600">
                          {t('Reject task')}
                        </InputLabel>
                      </div>
                      {isReject && (
                        <FormField
                          control={form.control}
                          name="rejectionNote"
                          render={({field}) => (
                            <FormItem>
                              <FormLabel>{t('Rejection note')}</FormLabel>
                              <FormControl>
                                <Textarea {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </DialogSection>
            <DialogFooter>
              {!!task && isReject && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={rejectTask.isPending}
                  >
                    {t('Submit')}
                  </Button>
                </>
              )}
              {(!task || !isReject) && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default PreviewTaskDialog;
